import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const token = () => localStorage.getItem('token');

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });

// ─── Status config ───────────────────────────────────────────────────────────
const STATUS_COLOURS = {
  awaiting_consent: ['#fef9c3', '#854d0e'],
  pending:          ['#fef3c7', '#92400e'],
  declined:         ['#fee2e2', '#991b1b'],
  accepted:         ['#dbeafe', '#1e40af'],
  completed:        ['#d1fae5', '#065f46'],
  paid:             ['#dcfce7', '#166534'],
  consent_expired:  ['#f3f4f6', '#6b7280'],
  cancelled:        ['#f3f4f6', '#6b7280'],
};
const STATUS_LABELS = {
  awaiting_consent: 'Awaiting Consent',
  pending:          'Pending',
  declined:         'Declined',
  accepted:         'Accepted',
  completed:        'Completed',
  paid:             'Paid',
  consent_expired:  'Consent Expired',
  cancelled:        'Cancelled',
};
const NEXT_STATUS = { pending: 'accepted', accepted: 'completed', completed: 'paid' };
const ALL_STATUSES = Object.keys(STATUS_COLOURS);

export default function ReferralsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('partners');

  // ── Partner Network state ────────────────────────────────────────────────
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [brochurePartner, setBrochurePartner] = useState(null); // partner shown in brochure modal
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [referralPartner, setReferralPartner] = useState(null);
  const [deals, setDeals] = useState([]);
  const [referralMode, setReferralMode] = useState('standalone'); // 'standalone' | 'deal'
  const [referralForm, setReferralForm] = useState({ deal_id: '', client_name: '', client_email: '', client_phone: '', notes: '', introducer_share_pct: '' });
  const [referralSaving, setReferralSaving] = useState(false);
  const [referralError, setReferralError] = useState('');
  const [referralSuccess, setReferralSuccess] = useState('');

  // ── Tracker state ────────────────────────────────────────────────────────
  const [referrals, setReferrals] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [updating, setUpdating] = useState({});
  const [commissionModal, setCommissionModal] = useState(null); // referral to update commission on
  const [commissionForm, setCommissionForm] = useState({ commission_amount: '', introducer_share_pct: '' });
  const [commissionSaving, setCommissionSaving] = useState(false);

  // ── Load partners ────────────────────────────────────────────────────────
  const loadPartners = useCallback(async () => {
    setPartnersLoading(true);
    try {
      const r = await API('/api/partners/available');
      const d = await r.json();
      setPartners(Array.isArray(d) ? d : []);
    } catch { setPartners([]); }
    setPartnersLoading(false);
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  // ── Load deals (for deal-tagging) ────────────────────────────────────────
  const loadDeals = useCallback(async () => {
    try {
      const r = await API('/api/deals');
      const d = await r.json();
      setDeals(Array.isArray(d) ? d : (d.deals || []));
    } catch { setDeals([]); }
  }, []);

  // ── Load referral tracker ────────────────────────────────────────────────
  const loadReferrals = useCallback(async () => {
    setTrackLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const r = await API(`/api/partners/referrals${qs}`);
      const d = await r.json();
      if (!r.ok) { setTrackError(d.error || 'Failed'); setTrackLoading(false); return; }
      setReferrals(Array.isArray(d) ? d : []);
    } catch { setTrackError('Network error'); }
    setTrackLoading(false);
  }, [statusFilter]);

  useEffect(() => { if (tab === 'tracker') loadReferrals(); }, [tab, loadReferrals]);

  const exportCsv = async () => {
    try {
      const res = await API('/api/partners/referrals/export');
      if (!res.ok) { alert('Export failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'partner-referrals-fca-log.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed'); }
  };

  // ── Open referral form ───────────────────────────────────────────────────
  const openReferralForm = async (partner) => {
    setReferralPartner(partner);
    setReferralMode('standalone');
    setReferralForm({ deal_id: '', client_name: '', client_email: '', client_phone: '', notes: '', introducer_share_pct: '' });
    setReferralError('');
    setReferralSuccess('');
    setShowReferralForm(true);
    setBrochurePartner(null);
    if (!deals.length) await loadDeals();
  };

  // ── Submit referral ──────────────────────────────────────────────────────
  const submitReferral = async (e) => {
    e.preventDefault();
    setReferralError('');
    setReferralSaving(true);

    let body = { partner_id: referralPartner.id, notes: referralForm.notes };

    if (referralMode === 'deal' && referralForm.deal_id) {
      // Tag to existing deal — pull client details from deal
      const deal = deals.find(d => String(d.id) === String(referralForm.deal_id));
      body.deal_id = referralForm.deal_id;
      body.client_name = deal?.applicant_name || deal?.client_name || referralForm.client_name;
      body.client_email = referralForm.client_email; // still required for consent email
      body.client_phone = referralForm.client_phone;
    } else {
      body.client_name = referralForm.client_name;
      body.client_email = referralForm.client_email;
      body.client_phone = referralForm.client_phone;
    }

    if (referralForm.introducer_share_pct) {
      body.introducer_share_pct = parseFloat(referralForm.introducer_share_pct);
    }

    try {
      const r = await API('/api/partners/referrals', { method: 'POST', body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { setReferralError(d.error || 'Failed to create referral'); setReferralSaving(false); return; }
      setReferralSuccess(`Referral created. A GDPR consent email has been sent to ${body.client_email}.`);
      setTimeout(() => { setShowReferralForm(false); setReferralSuccess(''); loadReferrals(); }, 3500);
    } catch { setReferralError('Network error'); }
    setReferralSaving(false);
  };

  // ── Advance referral status ──────────────────────────────────────────────
  const advance = async (ref) => {
    const next = NEXT_STATUS[ref.status];
    if (!next) return;
    setUpdating(u => ({ ...u, [ref.id]: true }));
    await fetch(`/api/partners/referrals/${ref.id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next }),
    });
    await loadReferrals();
    setUpdating(u => ({ ...u, [ref.id]: false }));
  };

  // ── Update commission amount ─────────────────────────────────────────────
  const saveCommission = async () => {
    setCommissionSaving(true);
    await fetch(`/api/partners/referrals/${commissionModal.id}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        commission_amount: commissionForm.commission_amount ? parseFloat(commissionForm.commission_amount) : undefined,
        introducer_share_pct: commissionForm.introducer_share_pct ? parseFloat(commissionForm.introducer_share_pct) : undefined,
      }),
    });
    setCommissionModal(null);
    setCommissionSaving(false);
    loadReferrals();
  };

  // ── Grouped partners by category ─────────────────────────────────────────
  const grouped = partners.reduce((acc, p) => {
    const key = p.category_name;
    if (!acc[key]) acc[key] = { icon: p.category_icon, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  // ── Tracker stats ────────────────────────────────────────────────────────
  const totalPaid = referrals
    .filter(r => r.status === 'paid' && r.commission_amount)
    .reduce((s, r) => s + parseFloat(r.commission_amount), 0);

  const commissionLabel = (ref) => {
    const cp = ref.agreed_commission_value ?? ref.commission_value;
    const ct = ref.agreed_commission_type ?? ref.commission_type;
    if (!cp) return '—';
    return ct === 'percentage' ? `${cp}%` : `£${cp}`;
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Partner Referrals</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Browse partners and track referrals</p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '28px', borderBottom: '1px solid #e5e7eb' }}>
          {[['partners', '🤝 Partner Network'], ['tracker', '📋 Referral Tracker']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontSize: '14px', fontWeight: tab === key ? 700 : 500,
              color: tab === key ? '#10b981' : '#6b7280',
              borderBottom: tab === key ? '2px solid #10b981' : '2px solid transparent',
              marginBottom: '-1px',
            }}>{label}</button>
          ))}
        </div>

        {/* ── PARTNER NETWORK TAB ───────────────────────────────────────── */}
        {tab === 'partners' && (
          <div>
            {partnersLoading ? (
              <div style={{ color: '#6b7280', padding: '40px 0' }}>Loading partners…</div>
            ) : partners.length === 0 ? (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤝</div>
                <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>No partners available yet</div>
                <div style={{ fontSize: '13px' }}>Your platform owner will enable partner services for your firm.</div>
              </div>
            ) : (
              Object.entries(grouped).map(([catName, { icon, items }]) => (
                <div key={catName} style={{ marginBottom: '32px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                    <span style={{ fontSize: '20px' }}>{icon}</span>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: '#0e1b2c' }}>{catName}</span>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>({items.length})</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
                    {items.map(p => {
                      const agreedVal = p.agreed_commission_value ?? p.commission_value;
                      const agreedType = p.agreed_commission_type ?? p.commission_type;
                      return (
                        <div key={p.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ fontWeight: 700, fontSize: '16px', color: '#0e1b2c' }}>{p.name}</div>
                            {agreedVal && (
                              <span style={{ background: '#d1fae5', color: '#065f46', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
                                {agreedType === 'percentage' ? `${agreedVal}% commission` : `£${agreedVal} commission`}
                              </span>
                            )}
                          </div>

                          {p.about && <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.55, margin: 0 }}>{p.about}</p>}

                          {p.usp_bullets && p.usp_bullets.length > 0 && (
                            <ul style={{ margin: 0, paddingLeft: '18px' }}>
                              {p.usp_bullets.slice(0, 3).map((b, i) => (
                                <li key={i} style={{ fontSize: '12px', color: '#374151', marginBottom: '3px' }}>{b}</li>
                              ))}
                            </ul>
                          )}

                          {p.services && p.services.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {p.services.map((s, i) => (
                                <span key={i} style={{ fontSize: '11px', background: '#f0f9ff', color: '#0369a1', padding: '2px 10px', borderRadius: '999px', border: '1px solid #bae6fd', fontWeight: 600 }}>
                                  {s.name}{s.price ? ` — ${s.price}` : ''}
                                </span>
                              ))}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '4px' }}>
                            <button
                              onClick={() => setBrochurePartner(p)}
                              style={{ flex: 1, padding: '8px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151' }}
                            >
                              View brochure
                            </button>
                            <button
                              onClick={() => openReferralForm(p)}
                              style={{ flex: 1, padding: '8px', border: 'none', borderRadius: '8px', background: '#10b981', cursor: 'pointer', fontSize: '13px', fontWeight: 700, color: '#fff' }}
                            >
                              Send referral
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── REFERRAL TRACKER TAB ─────────────────────────────────────── */}
        {tab === 'tracker' && (
          <div>
            {trackError && <div style={alertRed}>{trackError}</div>}

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '20px' }}>
              {[
                ['Total', referrals.length, '#e0e7ff', '#3730a3'],
                ['Awaiting consent', referrals.filter(r => r.status === 'awaiting_consent').length, '#fef9c3', '#854d0e'],
                ['In progress', referrals.filter(r => ['pending','accepted'].includes(r.status)).length, '#dbeafe', '#1e40af'],
                ['Commission earned', `£${totalPaid.toFixed(2)}`, '#d1fae5', '#065f46'],
              ].map(([label, value, bg, col]) => (
                <div key={label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '22px', fontWeight: 800, color: col }}>{value}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Status filter */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button onClick={() => setStatusFilter('')} style={filterBtn(!statusFilter)}>All ({referrals.length})</button>
              {ALL_STATUSES.filter(s => referrals.some(r => r.status === s)).map(s => {
                const [bg, col] = STATUS_COLOURS[s];
                const active = statusFilter === s;
                return (
                  <button key={s} onClick={() => setStatusFilter(s)} style={{ border: 'none', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 500, background: active ? col : bg, color: active ? '#fff' : col }}>
                    {STATUS_LABELS[s]} ({referrals.filter(r => r.status === s).length})
                  </button>
                );
              })}
              <button onClick={exportCsv} title="Download the full FCA referral & disclosure log as CSV" style={{ marginLeft: 'auto', border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '5px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#374151' }}>⬇ Export FCA log (CSV)</button>
            </div>

            {/* Table */}
            <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1fr 1.5fr', padding: '10px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Partner', 'Client', 'Deal', 'Status', 'Commission', 'Actions'].map(h => (
                  <div key={h} style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.04em' }}>{h}</div>
                ))}
              </div>

              {trackLoading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
              ) : referrals.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No referrals yet — use the Partner Network tab to send one</div>
              ) : (
                referrals
                  .filter(r => !statusFilter || r.status === statusFilter)
                  .map(ref => {
                    const [bg, col] = STATUS_COLOURS[ref.status] || ['#f3f4f6', '#6b7280'];
                    const next = NEXT_STATUS[ref.status];
                    return (
                      <div key={ref.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 1.2fr 1fr 1.5fr', padding: '13px 16px', borderBottom: '1px solid #f3f4f6', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '15px' }}>{ref.category_icon}</span>
                            <span style={{ fontWeight: 600, fontSize: '13px' }}>{ref.partner_name}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{ref.category_name}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', color: '#374151' }}>{ref.client_name}</div>
                          {ref.client_email && <div style={{ fontSize: '11px', color: '#9ca3af' }}>{ref.client_email}</div>}
                        </div>
                        <div>
                          {ref.deal_id ? (
                            <button onClick={() => navigate(`/deal/${ref.deal_id}`)}
                              style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: '12px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                              {ref.deal_applicant || 'View deal'}
                            </button>
                          ) : <span style={{ fontSize: '12px', color: '#9ca3af' }}>Standalone</span>}
                        </div>
                        <div>
                          <span style={{ background: bg, color: col, padding: '2px 8px', borderRadius: 999, fontSize: '11px', fontWeight: 700 }}>
                            {STATUS_LABELS[ref.status] || ref.status}
                          </span>
                        </div>
                        <div>
                          {ref.commission_amount ? (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: '#065f46' }}>£{parseFloat(ref.commission_amount).toFixed(2)}</div>
                              {ref.introducer_amount && <div style={{ fontSize: '11px', color: '#9ca3af' }}>Intro: £{parseFloat(ref.introducer_amount).toFixed(2)}</div>}
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: '12px', color: '#9ca3af' }}>{commissionLabel(ref)}</div>
                              {['accepted','completed'].includes(ref.status) && (
                                <button onClick={() => { setCommissionModal(ref); setCommissionForm({ commission_amount: '', introducer_share_pct: ref.introducer_share_pct || '' }); }}
                                  style={{ fontSize: '11px', color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: '2px' }}>
                                  Enter amount
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {next ? (
                            <button disabled={!!updating[ref.id]} onClick={() => advance(ref)}
                              style={{ background: '#0e1b2c', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {updating[ref.id] ? '…' : `Mark ${next}`}
                            </button>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                              {['cancelled','declined','consent_expired'].includes(ref.status) ? STATUS_LABELS[ref.status] : 'Complete'}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        )}

        {/* ── BROCHURE MODAL ────────────────────────────────────────────── */}
        {brochurePartner && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setBrochurePartner(null); }}>
            <div style={{ ...modalStyle, maxWidth: '560px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '20px' }}>{brochurePartner.category_icon}</span>
                    <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: '#0e1b2c' }}>{brochurePartner.name}</h2>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>{brochurePartner.category_name}</div>
                </div>
                <button onClick={() => setBrochurePartner(null)} style={closeBtn}>&times;</button>
              </div>

              {brochurePartner.about && (
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6, margin: 0 }}>{brochurePartner.about}</p>
                </div>
              )}

              {brochurePartner.usp_bullets && brochurePartner.usp_bullets.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={sectionLabel}>Key selling points</div>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {brochurePartner.usp_bullets.map((b, i) => (
                      <li key={i} style={{ fontSize: '13px', color: '#374151', marginBottom: '5px', lineHeight: 1.5 }}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}

              {brochurePartner.services && brochurePartner.services.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={sectionLabel}>Services offered</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {brochurePartner.services.map((s, i) => (
                      <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: '#0e1b2c' }}>{s.name}</div>
                          {s.price && <div style={{ fontSize: '12px', color: '#059669', fontWeight: 700 }}>{s.price}</div>}
                        </div>
                        {s.description && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '3px' }}>{s.description}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Commission */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#065f46', marginBottom: '4px' }}>Commission agreement</div>
                {brochurePartner.agreed_commission_value != null ? (
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#065f46' }}>
                    {brochurePartner.agreed_commission_type === 'percentage'
                      ? `${brochurePartner.agreed_commission_value}% of deal value`
                      : `£${brochurePartner.agreed_commission_value} per referral`}
                  </div>
                ) : (
                  <div style={{ fontSize: '13px', color: '#6b7280' }}>Default rate applies — contact your platform owner for details.</div>
                )}
                {brochurePartner.agreed_commission_notes && (
                  <div style={{ fontSize: '12px', color: '#374151', marginTop: '6px' }}>{brochurePartner.agreed_commission_notes}</div>
                )}
              </div>

              {/* Contact */}
              {(brochurePartner.contact_name || brochurePartner.contact_email || brochurePartner.website) && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={sectionLabel}>Contact</div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#374151' }}>
                    {brochurePartner.contact_name && <span>👤 {brochurePartner.contact_name}</span>}
                    {brochurePartner.contact_email && <a href={`mailto:${brochurePartner.contact_email}`} style={{ color: '#1d4ed8' }}>✉ {brochurePartner.contact_email}</a>}
                    {brochurePartner.website && <a href={brochurePartner.website} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>🌐 {brochurePartner.website.replace('https://', '')}</a>}
                  </div>
                </div>
              )}

              <button
                onClick={() => openReferralForm(brochurePartner)}
                style={{ width: '100%', padding: '12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
              >
                Send referral to {brochurePartner.name}
              </button>
            </div>
          </div>
        )}

        {/* ── REFERRAL FORM MODAL ───────────────────────────────────────── */}
        {showReferralForm && referralPartner && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget && !referralSuccess) setShowReferralForm(false); }}>
            <div style={{ ...modalStyle, maxWidth: '500px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Send Referral</h2>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                    {referralPartner.category_icon} {referralPartner.name}
                  </div>
                </div>
                {!referralSuccess && <button onClick={() => setShowReferralForm(false)} style={closeBtn}>&times;</button>}
              </div>

              {referralSuccess ? (
                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '8px', color: '#065f46' }}>Referral created</div>
                  <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.5 }}>{referralSuccess}</div>
                </div>
              ) : (
                <form onSubmit={submitReferral}>
                  {referralError && <div style={alertRed}>{referralError}</div>}

                  {/* GDPR notice */}
                  <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', marginBottom: '18px', fontSize: '12px', color: '#92400e', lineHeight: 1.5 }}>
                    <strong>GDPR consent flow:</strong> A consent email will be sent to the client before their details are shared with {referralPartner.name}. The referral will be active once they accept.
                  </div>

                  {/* Mode toggle */}
                  <div style={{ marginBottom: '18px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>Referral type</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[['standalone', '➕ Standalone client'], ['deal', '🔗 Tag to existing deal']].map(([m, label]) => (
                        <button key={m} type="button" onClick={() => setReferralMode(m)} style={{
                          flex: 1, padding: '8px', border: `2px solid ${referralMode === m ? '#10b981' : '#e5e7eb'}`,
                          borderRadius: '8px', background: referralMode === m ? '#f0fdf4' : '#fff',
                          cursor: 'pointer', fontSize: '12px', fontWeight: referralMode === m ? 700 : 500,
                          color: referralMode === m ? '#065f46' : '#6b7280',
                        }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Deal selector */}
                  {referralMode === 'deal' && (
                    <div style={fieldWrap}>
                      <label style={labelStyle}>Select deal</label>
                      <select
                        value={referralForm.deal_id}
                        onChange={e => {
                          const deal = deals.find(d => String(d.id) === e.target.value);
                          setReferralForm(f => ({
                            ...f,
                            deal_id: e.target.value,
                            client_name: deal?.applicant_name || deal?.client_name || f.client_name,
                          }));
                        }}
                        style={inputStyle}
                      >
                        <option value="">Select a deal…</option>
                        {deals.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.applicant_name || d.client_name || `Deal #${d.id}`}{d.mortgage_type ? ` — ${d.mortgage_type}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Client name */}
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Client full name *</label>
                    <input
                      required value={referralForm.client_name}
                      onChange={e => setReferralForm(f => ({ ...f, client_name: e.target.value }))}
                      style={inputStyle} placeholder="Jane Smith"
                    />
                  </div>

                  {/* Client email — always needed for consent */}
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Client email * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(for consent email)</span></label>
                    <input
                      required type="email" value={referralForm.client_email}
                      onChange={e => setReferralForm(f => ({ ...f, client_email: e.target.value }))}
                      style={inputStyle} placeholder="jane@example.com"
                    />
                  </div>

                  {/* Client phone */}
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Client phone</label>
                    <input
                      value={referralForm.client_phone}
                      onChange={e => setReferralForm(f => ({ ...f, client_phone: e.target.value }))}
                      style={inputStyle} placeholder="07700 900000"
                    />
                  </div>

                  {/* Introducer share */}
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Introducer share % <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional — if sharing commission with introducer)</span></label>
                    <input
                      type="number" min="0" max="100" step="0.1"
                      value={referralForm.introducer_share_pct}
                      onChange={e => setReferralForm(f => ({ ...f, introducer_share_pct: e.target.value }))}
                      style={inputStyle} placeholder="e.g. 20"
                    />
                  </div>

                  {/* Notes */}
                  <div style={fieldWrap}>
                    <label style={labelStyle}>Notes for partner</label>
                    <textarea
                      value={referralForm.notes}
                      onChange={e => setReferralForm(f => ({ ...f, notes: e.target.value }))}
                      style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
                      placeholder="Any context or requirements the partner should know…"
                    />
                  </div>

                  <button type="submit" disabled={referralSaving} style={{ width: '100%', padding: '11px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', marginTop: '4px' }}>
                    {referralSaving ? 'Sending…' : 'Send referral & consent request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}

        {/* ── COMMISSION ENTRY MODAL ────────────────────────────────────── */}
        {commissionModal && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setCommissionModal(null); }}>
            <div style={{ ...modalStyle, maxWidth: '380px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '17px', fontWeight: 700, margin: 0 }}>Enter commission received</h2>
                <button onClick={() => setCommissionModal(null)} style={closeBtn}>&times;</button>
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                {commissionModal.partner_name} → {commissionModal.client_name}
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Commission amount (£) *</label>
                <input type="number" step="0.01" min="0" value={commissionForm.commission_amount}
                  onChange={e => setCommissionForm(f => ({ ...f, commission_amount: e.target.value }))}
                  style={inputStyle} placeholder="e.g. 250.00" autoFocus />
              </div>
              <div style={fieldWrap}>
                <label style={labelStyle}>Introducer share % <span style={{ fontWeight: 400, color: '#9ca3af' }}>(auto-calculates amount)</span></label>
                <input type="number" step="0.1" min="0" max="100" value={commissionForm.introducer_share_pct}
                  onChange={e => setCommissionForm(f => ({ ...f, introducer_share_pct: e.target.value }))}
                  style={inputStyle} placeholder={commissionModal.introducer_share_pct || ''} />
              </div>
              <button onClick={saveCommission} disabled={commissionSaving || !commissionForm.commission_amount} style={{ width: '100%', padding: '10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                {commissionSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const modalStyle = { background: '#fff', borderRadius: '14px', padding: '24px', maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' };
const closeBtn = { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, flexShrink: 0 };
const fieldWrap = { marginBottom: '14px' };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', fontFamily: 'inherit', color: '#111827', boxSizing: 'border-box' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' };
const sectionLabel = { fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '10px' };
const filterBtn = (active) => ({ border: 'none', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: active ? 700 : 500, background: active ? '#0e1b2c' : '#e5e7eb', color: active ? '#fff' : '#374151' });
